import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal, ModalBody, ModalSection, Input, Spinner, MultiSelect } from '@components/ui';
import { CTA, GHOST } from '@components/ui/designTokens';
import { UserPlusIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useCreateUser, useRoles, useAssignableCompanies } from '../hooks/useUsers';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { useAuthStore } from '@features/auth/store/authStore';
import { normalizeRole } from '@lib/utils/roleNavigation';
// SUBSCRIPTIONS_DISABLED: import { useConsumption } from '@features/subscriptions/hooks';
import Swal from 'sweetalert2';

const createUserSchema = z.object({
  email: z.string().email('Email inválido').max(100, 'Máximo 100 caracteres'),
  idNumber: z.string().min(1, 'La cédula es requerida').max(20, 'Máximo 20 caracteres'),
  firstName: z.string().min(1, 'El nombre es requerido').max(100, 'Máximo 100 caracteres'),
  lastName: z.string().min(1, 'El apellido es requerido').max(100, 'Máximo 100 caracteres'),
  roles: z.array(z.string()).min(1, 'Debe seleccionar al menos un rol'),
  companies: z.array(z.string()).min(1, 'Debe seleccionar al menos una compañía'),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateUserModal({ isOpen, onClose }: CreateUserModalProps) {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  // Si quien está creando el usuario tiene el rol Internal Admin, también
  // puede asignar "SUPER ADMIN" — mismo patrón de gate por rol de sesión que
  // ya usa BannerAdminPage.tsx/UserManagerPage.tsx (columna ID).
  const sessionUser = useAuthStore((s) => s.user);
  const esInternalAdmin = normalizeRole(sessionUser?.role) === 'INTERNALADMIN';

  // Roles permitidos para crear usuarios
  const ALLOWED_ROLES = [
    'VENDEDOR', 'CUSTOMER B2B', 'COMPANY ADMIN', 'ADMINISTRATOR',
    ...(esInternalAdmin ? ['SUPER ADMIN'] : []),
  ];

  const { data: rolesData, isLoading: loadingRoles } = useRoles();
  const { selectedCompany } = useCompanies({ navigateOnSelect: false });
  const { data: assignableCompanies, isLoading: loadingCompanies } = useAssignableCompanies();
  // La compañía activa de la sesión siempre debe aparecer como opción, aunque
  // por alguna razón no venga en la lista "asignable" del admin — es la
  // compañía preseleccionada por defecto y el admin debe poder verla/dejarla.
  // Memoizado para no disparar en bucle el useEffect que depende de esta lista.
  const companies = useMemo(() => {
    const list = assignableCompanies || [];
    if (selectedCompany && !list.some((c) => c.id === selectedCompany.id)) {
      return [{ id: selectedCompany.id, name: selectedCompany.name, code: selectedCompany.code || '' }, ...list];
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignableCompanies, selectedCompany?.id]);
  const createMutation = useCreateUser();

  /* SUBSCRIPTIONS_DISABLED
  const {
    validateCanAddUser,
    consumption,
    currentPlan,
    getRemainingUsers,
    isLoading: isLoadingConsumption,
  } = useConsumption();
  */

  // Filtrar solo los roles permitidos (case-insensitive) y eliminar duplicados por nombre
  const filteredRoles = rolesData?.filter(role =>
    ALLOWED_ROLES.some(allowedRole => allowedRole.toUpperCase() === role.name.toUpperCase())
  ).reduce((unique, role) => {
    // Only add if we haven't seen this role name before (case-insensitive)
    if (!unique.some(r => r.name.toUpperCase() === role.name.toUpperCase())) {
      unique.push(role);
    }
    return unique;
  }, [] as typeof rolesData) || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
  });

  // Por defecto se preselecciona la compañía activa de la sesión, igual que el
  // portal viejo (_SelectagCompany.cshtml) — el admin puede agregar más
  // compañías de entre las que él mismo tiene acceso.
  useEffect(() => {
    if (isOpen && selectedCompany && selectedCompanies.length === 0) {
      setSelectedCompanies([selectedCompany.id]);
      setValue('companies', [selectedCompany.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedCompany]);

  const onSubmit = async (data: CreateUserFormData) => {
    try {
      /* SUBSCRIPTIONS_DISABLED
      const validation = validateCanAddUser();
      if (!validation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de usuarios alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });
        if (result.isConfirmed) {
          window.location.href = '/subscriptions/plans';
        }
        return;
      }
      */

      await createMutation.mutateAsync({
        ...data,
        roles: selectedRoles,
        branchOffices: [],
        companies: selectedCompanies,
      });

      await Swal.fire({
        title: 'Usuario creado',
        text: 'El usuario ha sido creado exitosamente. Se ha enviado un correo con las instrucciones para establecer la contraseña.',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });

      reset();
      setSelectedRoles([]);
      setSelectedCompanies([]);
      onClose();
    } catch {
      // Sin diálogo acá a propósito. El `onError` de `useCreateUser` ya abre el
      // modal de error del SupportErrorProvider —el modal único del portal, con
      // el Asistente IA— y la traducción del mensaje se movió allí. Este
      // `catch` existe solo para que el rechazo de la mutación no burbujee y
      // deje el formulario en un estado raro; el modal de creación queda
      // abierto para que se pueda corregir el dato y reintentar.
    }
  };

  const handleClose = () => {
    reset();
    setSelectedRoles([]);
    setSelectedCompanies([]);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Crear Nuevo Usuario"
      kicker="Accesos y permisos"
      description="Configura los datos, roles y alcance del nuevo usuario. Se enviará una invitación al correo para establecer la contraseña."
      icon={<UserPlusIcon />}
      size="lg"
    >
      <ModalBody className="h-auto max-h-[75vh] overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-7">
          {/* ===== 01 · Alcance ===== */}
          <div>
            <ModalSection index="01" title="Alcance" />
            <p className="mb-3 flex items-start gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
              <InformationCircleIcon className="mt-[1px] h-3.5 w-3.5 flex-shrink-0 text-secondary-400" />
              Si el usuario no queda con ninguna compañía asignada, no podrá iniciar sesión en el portal.
            </p>
            <MultiSelect
              label="Compañías *"
              options={companies.map((company) => ({
                id: company.id,
                // `company.name` puede venir `null` desde Aludra — ver
                // CompanyCard.tsx/MultiSelect.tsx (mismo problema).
                label: company.name || company.code || 'Sin nombre',
                description: company.code,
              }))}
              selected={selectedCompanies}
              onChange={(values) => {
                setSelectedCompanies(values);
                setValue('companies', values);
              }}
              placeholder="Selecciona una o más compañías"
              data-testid="user-create-modal-companies-select"
              error={errors.companies?.message}
              loading={loadingCompanies}
            />
          </div>

            {/* SUBSCRIPTIONS_DISABLED
            {!isLoadingConsumption && consumption && currentPlan && (
              <div className="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-lg border border-primary-200 dark:border-primary-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-primary-900 dark:text-primary-100 mb-1">
                      Plan Actual: {currentPlan.name}
                    </h3>
                    <p className="text-xs text-primary-700 dark:text-primary-300">
                      {consumption.UserQty} de {currentPlan.limits.usuarios === -1 ? 'ilimitados' : currentPlan.limits.usuarios} usuarios utilizados
                    </p>
                  </div>
                  {(() => {
                    const remaining = getRemainingUsers();
                    if (remaining === -1) {
                      return (
                        <div className="text-right">
                          <p className="text-lg font-semibold text-primary-900 dark:text-primary-100">∞</p>
                          <p className="text-xs text-primary-700 dark:text-primary-300">ilimitado</p>
                        </div>
                      );
                    }
                    if (remaining !== null && !isNaN(remaining)) {
                      return (
                        <div className="text-right">
                          <p className="text-lg font-semibold text-primary-900 dark:text-primary-100">{remaining}</p>
                          <p className="text-xs text-primary-700 dark:text-primary-300">restantes</p>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            )}
            */}

          {/* ===== 02 · Información personal ===== */}
          <div>
            <ModalSection index="02" title="Información personal" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="Nombre *"
                placeholder="Juan"
                data-testid="user-create-modal-first-name-input"
                error={errors.firstName?.message}
                maxLength={100}
                {...register('firstName')}
              />
              <Input
                label="Apellido *"
                placeholder="Pérez"
                data-testid="user-create-modal-last-name-input"
                error={errors.lastName?.message}
                maxLength={100}
                {...register('lastName')}
              />
              <Input
                label="IDNumber *"
                placeholder="8-888-8888"
                data-testid="user-create-modal-id-number-input"
                error={errors.idNumber?.message}
                maxLength={20}
                {...register('idNumber')}
              />
              <Input
                label="Email *"
                type="email"
                placeholder="usuario@ejemplo.com"
                data-testid="user-create-modal-email-input"
                error={errors.email?.message}
                maxLength={100}
                {...register('email')}
              />
            </div>
          </div>

          {/* ===== 03 · Roles ===== */}
          <div>
            <ModalSection index="03" title="Roles" />
            <p className="mb-3 flex items-start gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
              <InformationCircleIcon className="mt-[1px] h-3.5 w-3.5 flex-shrink-0 text-secondary-400" />
              Si el usuario no tiene ninguno de estos roles asignados, no podrá iniciar sesión en el portal.
            </p>
            <div className="space-y-5">
              <MultiSelect
                label="Roles *"
                options={filteredRoles.map((role, index) => ({
                  id: role.id || `role-${role.name}-${index}`,
                  label: role.name,
                  description: role.description,
                }))}
                selected={selectedRoles}
                onChange={(values) => {
                  setSelectedRoles(values);
                  setValue('roles', values);
                }}
                placeholder="Selecciona los roles"
                data-testid="user-create-modal-roles-select"
                error={errors.roles?.message}
                loading={loadingRoles}
              />
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2.5 border-t border-[#ececec] pt-5 dark:border-secondary-700">
            <button
              type="button"
              onClick={handleClose}
              data-testid="user-create-modal-cancel-button"
              disabled={createMutation.isPending}
              className={GHOST}
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="user-create-modal-submit-button"
              disabled={createMutation.isPending || loadingRoles || loadingCompanies}
              className={CTA}
            >
              {createMutation.isPending ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creando...
                </>
              ) : (
                'Crear Usuario'
              )}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
}
